import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Divider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
} from '@mui/material';
import {
  Psychology,
  Refresh,
  ExpandMore,
  ChatBubbleOutline,
  Lightbulb,
  Favorite,
  Warning,
  CheckCircle,
  Cancel,
  ArrowForward,
} from '@mui/icons-material';
import { AIGeneratedInsights } from '../types';

interface AIInsightsProps {
  insights: AIGeneratedInsights;
  onRefresh?: () => void;
  lastFetched?: string;
  source?: string;
}

export const AIInsights: React.FC<AIInsightsProps> = ({ insights, onRefresh, lastFetched, source }) => {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Psychology sx={{ color: '#ec4899', fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            AI-Generated Intelligence
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onRefresh && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={onRefresh}
            >
              Refresh
            </Button>
          )}
        </Box>
      </Box>
      {(lastFetched || source) && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, mb: 2 }}>
          {lastFetched && (
            <Typography variant="caption" color="text.secondary">
              Updated: {new Date(lastFetched).toLocaleString()}
            </Typography>
          )}
          {source && (
            <Chip label={source === 'openai' ? 'OpenAI' : source} size="small" color="info" />
          )}
        </Box>
      )}

      {/* Persona Card */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2,
          boxShadow: 2,
          bgcolor: '#fef9e7',
          border: '1px solid #fde68a',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Chip
                label={insights.persona.type}
                color="warning"
                sx={{ mb: 1, fontWeight: 'bold' }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                JUST FOR PHYSICIANS
              </Typography>
            </Box>
          </Box>
          <Typography variant="body1" paragraph>
            {insights.persona.summary}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                <ChatBubbleOutline color="primary" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
                    Communication Style
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {insights.persona.communicationStyle}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                <Lightbulb color="primary" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
                    Decision Drivers
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    {insights.persona.decisionDrivers.map((driver, index) => (
                      <ListItem key={index} sx={{ py: 0, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 20 }}>
                          <ArrowForward fontSize="small" color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={driver}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Favorite color="error" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
                    Motivations
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    {insights.persona.motivations.map((motivation, index) => (
                      <ListItem key={index} sx={{ py: 0, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 20 }}>
                          <ArrowForward fontSize="small" color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={motivation}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Warning color="warning" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
                    Concerns
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    {insights.persona.concerns.map((concern, index) => (
                      <ListItem key={index} sx={{ py: 0, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 20 }}>
                          <ArrowForward fontSize="small" color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={concern}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Engagement Insights */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: 1,
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" color="white" fontWeight="bold">
                📊
              </Typography>
            </Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Engagement Insights
            </Typography>
          </Box>
          <List>
            {insights.engagementInsights.map((insight, index) => (
              <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 30 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      bgcolor: '#ff9800',
                      borderRadius: '50%',
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={insight.text}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Recommended Discussion Areas */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" mb={2}>
            Recommended Discussion Areas
          </Typography>
          <Stack spacing={2}>
            {insights.recommendedDiscussions.map((discussion, index) => (
              <Accordion key={index} sx={{ boxShadow: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="body2" fontWeight="medium">
                    {discussion.topic}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary">
                        Rationale:
                      </Typography>
                      <Typography variant="body2">{discussion.rationale}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary">
                        Approach:
                      </Typography>
                      <Typography variant="body2">{discussion.approach}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary">
                        Timing:
                      </Typography>
                      <Typography variant="body2">{discussion.timing}</Typography>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Do's and Don'ts */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" mb={2}>
            Do's and Don'ts
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold">
                  Do's
                </Typography>
              </Box>
              <List dense>
                {insights.dosAndDonts.dos.map((doItem, index) => (
                  <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={doItem}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <Cancel color="error" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold">
                  Don'ts
                </Typography>
              </Box>
              <List dense>
                {insights.dosAndDonts.donts.map((dontItem, index) => (
                  <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <Cancel color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={dontItem}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Next Best Action */}
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: 2,
          bgcolor: 'primary.main',
          color: 'white',
        }}
      >
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            Next Best Action
          </Typography>
          <Typography variant="body1">{insights.nextBestAction}</Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

